import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDatabase, useUpdateDatabase, useDeleteDatabase } from '../hooks/useDatabases';
import { DeleteConfirmationModal } from '@/shared/components/ui/DeleteConfirmationModal';
import { Input } from '@/shared/components/ui/Input';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/shared/icons'
import type { IconName } from '@/shared/icons'
export interface DatabaseSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  databaseId: string;
}

type TabType = 'general' | 'domains';

/**
 * Modal de configurações do banco de dados
 *
 * Segue o padrão always-light com valores hardcoded para cores
 * Possui duas abas: Informações Gerais e Domínios
 */
export const DatabaseSettingsModal: React.FC<DatabaseSettingsModalProps> = ({
  isOpen,
  onClose,
  databaseId,
}) => {
  const navigate = useNavigate();
  const { data: database, isLoading: isLoadingDatabase } = useDatabase(databaseId);
  const updateMutation = useUpdateDatabase();
  const deleteMutation = useDeleteDatabase();

  const [activeTab, setActiveTab] = useState<TabType>('general');
  const [name, setName] = useState('');
  const [domains, setDomains] = useState<string[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [domainError, setDomainError] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Sincronizar dados quando database carregar
  useEffect(() => {
    if (database) {
      setName(database.name);
      setDomains(database.domains || []);
    }
  }, [database]);

  // Reset tab when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab('general');
      setNewDomain('');
      setDomainError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const hasNameChanges = database && name !== database.name;
  const hasDomainsChanges = database && JSON.stringify(domains) !== JSON.stringify(database.domains || []);
  const hasChanges = hasNameChanges || hasDomainsChanges;
  const isValid = name.trim().length >= 3;

  const validateDomain = (domain: string): boolean => {
    // Validar como host de domínio (sem protocolo)
    const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    return domainRegex.test(domain);
  };

  const handleAddDomain = () => {
    const trimmedDomain = newDomain.trim();

    if (!trimmedDomain) {
      setDomainError('Digite um domínio');
      return;
    }

    if (!validateDomain(trimmedDomain)) {
      setDomainError('Digite um domínio válido (ex: exemplo.com)');
      return;
    }

    if (domains.includes(trimmedDomain)) {
      setDomainError('Este domínio já foi adicionado');
      return;
    }

    setDomains([...domains, trimmedDomain]);
    setNewDomain('');
    setDomainError('');
  };

  const handleRemoveDomain = (domainToRemove: string) => {
    setDomains(domains.filter(d => d !== domainToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddDomain();
    }
  };

  const handleSave = async () => {
    if (!hasChanges || !isValid) return;

    try {
      await updateMutation.mutateAsync({
        id: databaseId,
        data: {
          name: name.trim(),
          domains: domains
        }
      });
      onClose();
    } catch (error) {
      console.error('Erro ao atualizar banco de dados:', error);
    }
  };

  const handleDelete = async () => {
    try {
      // Navegar ANTES da exclusão para evitar que a página tente carregar o recurso excluído
      navigate('/databases', { replace: true });
      setIsDeleteModalOpen(false);
      onClose();
      // Executar a exclusão após navegar (o componente anterior já foi desmontado)
      await deleteMutation.mutateAsync(databaseId);
    } catch (error) {
      console.error('Erro ao excluir banco de dados:', error);
    }
  };

  const tabs = [
    { id: 'general' as TabType, label: 'Informações gerais', icon: 'info' },
    { id: 'domains' as TabType, label: 'Domínios', icon: 'globe' },
  ];

  return (
    <>
      {/* Modal Overlay */}
      {createPortal((
      <div
        className="fixed inset-0 flex items-center justify-center z-[60] p-4"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      >
        <div
          className="rounded-lg shadow-xl w-full max-w-md flex flex-col"
          style={{
            backgroundColor: '#ffffff',
            maxHeight: '90vh',
            '--theme-text': '#1e293b',
            '--theme-muted-text': '#6b7280',
            '--theme-input-background': '#ffffff',
            '--theme-input-text': '#1e293b',
            '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
            '--theme-input-focus': '#8b5cf6',
            '--theme-error-color': '#ef4444',
          } as React.CSSProperties}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Fixed Header */}
          <div
            className="flex items-center justify-between p-4 border-b flex-shrink-0"
            style={{ borderColor: '#e5e7eb' }}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ backgroundColor: '#f1f5f9' }}>
                <Icon name="settings" className="w-5 h-5" style={{ color: '#64748b' }} />
              </div>
              <h2
                className="text-lg font-semibold"
                style={{ color: '#1e293b' }}
              >
                Configurações
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded transition-colors"
              style={{ color: '#64748b' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f1f5f9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              disabled={updateMutation.isPending || deleteMutation.isPending}
            >
              <Icon name="x" className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs Navigation */}
          <div
            className="flex border-b flex-shrink-0"
            style={{ borderColor: '#e5e7eb' }}
          >
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors relative"
                  style={{
                    color: isActive ? '#8b5cf6' : '#64748b',
                    backgroundColor: 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <Icon name={tab.icon as IconName} className="w-4 h-4" />
                  {tab.label}
                  {isActive && (
                    <div
                      className="absolute bottom-0 left-0 right-0 h-0.5"
                      style={{ backgroundColor: '#8b5cf6' }}
                    />
                  )}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto flex-1">
            {isLoadingDatabase ? (
              <div className="flex items-center justify-center py-8">
                <div
                  className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
                  style={{ borderColor: '#8b5cf6', borderTopColor: 'transparent' }}
                />
              </div>
            ) : (
              <>
                {/* Tab: Informações Gerais */}
                {activeTab === 'general' && (
                  <div className="space-y-4">
                    {/* Nome do Banco */}
                    <div>
                      <label
                        className="block text-sm font-medium mb-2"
                        style={{ color: '#1e293b' }}
                      >
                        Nome do Banco
                      </label>
                      <Input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Digite o nome do banco de dados"
                        disabled={updateMutation.isPending}
                      />
                      {name.trim().length > 0 && name.trim().length < 3 && (
                        <p className="text-xs mt-1" style={{ color: '#ef4444' }}>
                          O nome deve ter pelo menos 3 caracteres
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Tab: Domínios */}
                {activeTab === 'domains' && (
                  <div className="space-y-4">
                    <div>
                      <label
                        className="block text-sm font-medium mb-2"
                        style={{ color: '#1e293b' }}
                      >
                        Domínios
                      </label>
                      <p
                        className="text-xs mb-3"
                        style={{ color: '#64748b' }}
                      >
                        Adicione os domínios que terão acesso a este banco de dados.
                      </p>

                      {/* Input para adicionar novo domínio */}
                      <div className="flex gap-2 mb-3">
                        <div className="flex-1">
                          <Input
                            type="text"
                            value={newDomain}
                            onChange={(e) => {
                              setNewDomain(e.target.value);
                              setDomainError('');
                            }}
                            onKeyPress={handleKeyPress}
                            error={domainError || undefined}
                            placeholder="exemplo.com"
                            disabled={updateMutation.isPending}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleAddDomain}
                          className="px-3 py-2 rounded-lg transition-colors flex items-center justify-center"
                          style={{
                            backgroundColor: '#8b5cf6',
                            color: '#ffffff',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#7c3aed';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#8b5cf6';
                          }}
                          disabled={updateMutation.isPending}
                        >
                          <Icon name="plus" className="w-5 h-5" />
                        </button>
                      </div>
                      {domainError && (
                        <p className="text-xs mb-3" style={{ color: '#ef4444' }}>
                          {domainError}
                        </p>
                      )}

                      {/* Lista de domínios */}
                      {domains.length === 0 ? (
                        <div
                          className="text-center py-6 rounded-lg border-2 border-dashed"
                          style={{ borderColor: '#e5e7eb' }}
                        >
                          <Icon name="globe" className="w-8 h-8 mx-auto mb-2"
                            style={{ color: '#9ca3af' }} />
                          <p
                            className="text-sm"
                            style={{ color: '#6b7280' }}
                          >
                            Nenhum domínio adicionado
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {domains.map((domain, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between px-3 py-2 rounded-lg"
                              style={{ backgroundColor: '#f8fafc' }}
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <Icon name="globe" className="w-4 h-4 flex-shrink-0"
                                  style={{ color: '#64748b' }} />
                                <span
                                  className="text-sm truncate"
                                  style={{ color: '#1e293b' }}
                                >
                                  {domain}
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveDomain(domain)}
                                className="p-1 rounded transition-colors flex-shrink-0 ml-2"
                                style={{ color: '#ef4444' }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#fef2f2';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                                disabled={updateMutation.isPending}
                              >
                                <Icon name="x" className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Fixed Footer */}
          <div
            className="flex items-center justify-between p-4 border-t flex-shrink-0"
            style={{
              backgroundColor: '#f1f5f9',
              borderColor: '#e5e7eb',
            }}
          >
            {/* Botão Excluir - Alinhado à esquerda */}
            <button
              type="button"
              onClick={() => setIsDeleteModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors"
              style={{
                color: '#ef4444',
                backgroundColor: 'transparent',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#fef2f2';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              disabled={updateMutation.isPending || deleteMutation.isPending || isLoadingDatabase}
            >
              <Icon name="trash" className="w-4 h-4" />
              Excluir
            </button>

            {/* Botões Cancelar e Salvar - Alinhados à direita */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium rounded-lg border transition-colors"
                style={{
                  color: '#1e293b',
                  backgroundColor: '#ffffff',
                  borderColor: '#e5e7eb',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f1f5f9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }}
                disabled={updateMutation.isPending}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: '#8b5cf6',
                }}
                onMouseEnter={(e) => {
                  if (!updateMutation.isPending && hasChanges && isValid) {
                    e.currentTarget.style.backgroundColor = '#7c3aed';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!updateMutation.isPending && hasChanges && isValid) {
                    e.currentTarget.style.backgroundColor = '#8b5cf6';
                  }
                }}
                disabled={updateMutation.isPending || !hasChanges || !isValid}
              >
                {updateMutation.isPending ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      </div>
      ), document.body)}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Excluir Banco de Dados"
        message={`Tem certeza que deseja excluir o banco "${database?.name || ''}"?`}
        warningMessage="Todos os dados associados a este banco serão perdidos permanentemente."
        isLoading={deleteMutation.isPending}
        confirmButtonText="Excluir Banco"
        cancelButtonText="Cancelar"
      />
    </>
  );
};

DatabaseSettingsModal.displayName = 'DatabaseSettingsModal';
